import { isDelayValid } from '../isDelayValid';

/**
 * Validates if a forward notifications block is properly configured and ready for execution.
 *
 * A forward notifications block is considered valid when ALL of the following conditions are met:
 * 1. Block exists and has a fields array
 * 2. Block has blockOptions.method.args defined
 * 3. Block has a valid delay configuration (via isDelayValid)
 * 4. All required method arguments have corresponding fields with non-empty values
 *
 * The function checks that every argument specified in blockOptions.method.args has a matching
 * field in the fields array (by name) and that field has a truthy value.
 *
 * @param {Object} currentBlock - The forward notifications block object to validate
 * @param {string} currentBlock._id - Unique identifier for the block
 * @param {Array} currentBlock.fields - Array of field objects with name, type, and value properties
 * @param {string} currentBlock.fields[].name - Field identifier matching args keys
 * @param {string} currentBlock.fields[].type - Field type (e.g., 'templateId', 'device', 'array')
 * @param {*} currentBlock.fields[].value - Field value (string, object, array, etc.)
 * @param {Object} currentBlock.blockOptions - Block configuration options
 * @param {Object} currentBlock.blockOptions.method - Method configuration
 * @param {string} currentBlock.blockOptions.method.name - Method name (typically 'forwardNotifications')
 * @param {Object|Array} currentBlock.blockOptions.method.args - Method arguments to validate
 * @param {Object} [currentBlock.delay] - Optional delay configuration
 * @param {string} currentBlock.blockType - Block type (e.g., 'then')
 *
 * @returns {boolean} Returns true if block is valid and ready for execution, false otherwise
 *
 * @example
 * // Valid forward notifications block (based on real example)
 * const validBlock = {
 *   _id: '68ca8078e88abe1f815d9ca9',
 *   blockOptions: {
 *     method: {
 *       args: {
 *         device: 'device',
 *         item: 'item',
 *         notificationBlock: 'notificationBlock',
 *         notificationTemplate: 'notificationTemplate',
 *         targets: 'targets'
 *       },
 *       name: 'forwardNotifications'
 *     }
 *   },
 *   blockType: 'then',
 *   fields: [
 *     { name: 'notificationTemplate', type: 'templateId', value: 'aba3b85a-9485-44b2-836f-c6319ed275e9' },
 *     { name: 'targets', type: 'array', value: [{ channels: ['email', 'push'], type: 'account' }] },
 *     { name: 'device', type: 'device', value: '68ca8077e88abe1db3f6242e' },
 *     { name: 'item', type: 'item', value: '68ca8077e88abe1db3f62431' },
 *     { name: 'notificationBlock', type: 'notificationBlock', value: { body: 'Alert message', subject: 'Alert' } }
 *   ]
 * };
 * validationStateForwardNotificationsIsValid(validBlock); // true
 *
 * @example
 * // Invalid block - missing required field
 * const incompleteBlock = {
 *   blockOptions: { method: { args: { device: 'device', targets: 'targets' } } },
 *   fields: [
 *     { name: 'device', type: 'device', value: '68ca8077e88abe1db3f6242e' }
 *     // missing 'targets' field
 *   ]
 * };
 * validationStateForwardNotificationsIsValid(incompleteBlock); // false
 *
 * @example
 * // Valid block with delay configuration
 * const delayBlock = {
 *   blockOptions: { method: { args: { device: 'device' } } },
 *   fields: [{ name: 'device', type: 'device', value: '12345' }],
 *   delay: { minutes: 5, seconds: 0 }
 * };
 * validationStateForwardNotificationsIsValid(delayBlock); // true (if delay is valid)
 *
 * @example
 * // Invalid cases
 * validationStateForwardNotificationsIsValid(null); // false
 * validationStateForwardNotificationsIsValid({}); // false
 * validationStateForwardNotificationsIsValid({ fields: [] }); // false
 */
export const validationStateForwardNotificationsIsValid = (currentBlock) => {
    if (
        currentBlock &&
        currentBlock.fields &&
        currentBlock.blockOptions?.method?.args &&
        isDelayValid([currentBlock])
    ) {
        return Object.values(currentBlock.blockOptions.method.args).every((arg) => {
            const argField = currentBlock.fields.find((field) => field?.name === arg);

            return argField?.value;
        });
    }

    return false;
};
